import { buildI18n } from "@repo/shared";
import type { LocalizedString } from "@repo/shared/types";
import {
  ALL_CLIENT_TYPES,
  type MandateMode,
  normalizeAllowedClientTypes,
  type PortfolioCollectionGoalType,
} from "@repo/shared/types";
import { ArrowDown, ArrowUp, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { InlineSvg } from "@/components/ui/inline-svg";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SvgIconField } from "@/components/ui/svg-icon-field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { TranslationButton } from "@/components/ui/translation-button";
import {
  useCreateGoalType,
  useDeleteGoalType,
  useReorderGoalTypes,
  useUpdateGoalType,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./goal-types-table.i18n";

/** Small inline SVG preview — raw designer-authored markup (trusted admins). */
function SvgPreview({ svg, className }: { svg: string | null; className?: string }) {
  if (!svg?.trim()) return null;
  return (
    <InlineSvg
      svg={svg}
      className={className ?? "inline-block h-6 w-6 [&_svg]:h-full [&_svg]:w-full"}
    />
  );
}

type GoalTypesTableProps = {
  portfolioCollectionId: string;
  goalTypes: PortfolioCollectionGoalType[];
  /** QL-375 — the Mandate column and field only show while the collection has mandates on. */
  mandatesEnabled: boolean;
};

export function GoalTypesTable({
  portfolioCollectionId,
  goalTypes,
  mandatesEnabled,
}: GoalTypesTableProps) {
  const createGoalType = useCreateGoalType();
  const updateGoalType = useUpdateGoalType();
  const deleteGoalType = useDeleteGoalType();
  const reorderGoalTypes = useReorderGoalTypes();

  const isPending =
    createGoalType.isPending ||
    updateGoalType.isPending ||
    deleteGoalType.isPending ||
    reorderGoalTypes.isPending;

  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const getLabel = (i18n: LocalizedString) => localize(i18n);

  // Dialog state (shared for create + edit)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<PortfolioCollectionGoalType | null>(null);
  const [formLabel, setFormLabel] = useState("");
  const [formIconSvg, setFormIconSvg] = useState("");
  // QL-36 — the form always holds an explicit list; null (= all) is expanded on
  // open and collapsed back to null on save by normalizeAllowedClientTypes.
  const [formClientTypes, setFormClientTypes] = useState<string[]>([...ALL_CLIENT_TYPES]);
  const [formMandateMode, setFormMandateMode] = useState<MandateMode>("hidden");
  const [formError, setFormError] = useState<string | null>(null);

  const mandateModeLabel = (mode: MandateMode) =>
    mode === "required" ? t.mandateRequired : t.mandateHidden;

  const editingTypeName = editingType ? getLabel(editingType.labelI18n) : "";

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<string | null>(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  const openCreate = () => {
    setEditingType(null);
    setFormLabel("");
    setFormIconSvg("");
    setFormClientTypes([...ALL_CLIENT_TYPES]);
    setFormMandateMode("hidden");
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (gt: PortfolioCollectionGoalType) => {
    setEditingType(gt);
    setFormLabel(getLabel(gt.labelI18n));
    setFormIconSvg(gt.iconSvg ?? "");
    setFormClientTypes(
      gt.allowedClientTypes && gt.allowedClientTypes.length > 0
        ? ALL_CLIENT_TYPES.filter((ct) => gt.allowedClientTypes?.includes(ct))
        : [...ALL_CLIENT_TYPES],
    );
    setFormMandateMode(gt.mandateMode ?? "hidden");
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    const label = formLabel.trim();
    if (!label) {
      setFormError(t.labelRequired);
      return;
    }
    setFormError(null);

    if (editingType) {
      // Merge existing i18n with the updated value for the current language.
      const labelI18n: LocalizedString = { ...editingType.labelI18n, [userLanguage]: label };
      updateGoalType.mutate(
        {
          collectionId: portfolioCollectionId,
          gtId: editingType.id,
          data: {
            labelI18n,
            iconSvg: formIconSvg.trim() || null,
            allowedClientTypes: normalizeAllowedClientTypes(formClientTypes),
            mandateMode: formMandateMode,
          },
        },
        {
          onSuccess: () => setDialogOpen(false),
          onError: (err: Error) =>
            showAlert(t.error, err.message || t.failedToUpdate, "destructive"),
        },
      );
    } else {
      createGoalType.mutate(
        {
          collectionId: portfolioCollectionId,
          data: {
            labelI18n: buildI18n(label, userLanguage, tenantSupportedLanguages),
            iconSvg: formIconSvg.trim() || null,
            allowedClientTypes: normalizeAllowedClientTypes(formClientTypes),
            mandateMode: formMandateMode,
          },
        },
        {
          onSuccess: () => setDialogOpen(false),
          onError: (err: Error) => showAlert(t.error, err.message || t.failedToAdd, "destructive"),
        },
      );
    }
  };

  const handleTranslationSave = (gt: PortfolioCollectionGoalType, labelI18n: LocalizedString) => {
    updateGoalType.mutate(
      {
        collectionId: portfolioCollectionId,
        gtId: gt.id,
        // QL-36 — a translation-only save must carry availability through, or
        // the update would reset it to "all client types".
        data: {
          labelI18n,
          iconSvg: gt.iconSvg,
          allowedClientTypes: gt.allowedClientTypes,
          mandateMode: gt.mandateMode,
        },
      },
      {
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToUpdate, "destructive"),
      },
    );
  };

  const handleDelete = () => {
    if (!typeToDelete) return;
    deleteGoalType.mutate(
      { collectionId: portfolioCollectionId, gtId: typeToDelete },
      {
        onSuccess: () => setTypeToDelete(null),
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToDelete, "destructive");
          setTypeToDelete(null);
        },
      },
    );
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= goalTypes.length) return;
    const orderedIds = goalTypes.map((gt) => gt.id);
    const [moved] = orderedIds.splice(index, 1);
    orderedIds.splice(target, 0, moved!);
    reorderGoalTypes.mutate(
      { collectionId: portfolioCollectionId, orderedIds },
      {
        onError: (err: Error) =>
          showAlert(t.error, err.message || t.failedToReorder, "destructive"),
      },
    );
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">{t.description}</p>
          <Button onClick={openCreate} disabled={isPending}>
            {t.addGoalType}
          </Button>
        </div>

        {goalTypes.length === 0 ? (
          <EmptyState>{t.noGoalTypes}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">{t.order}</TableHead>
                <TableHead>{t.label}</TableHead>
                <TableHead>{t.icon}</TableHead>
                {mandatesEnabled && <TableHead>{t.mandate}</TableHead>}
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {goalTypes.map((gt, index) => (
                <TableRow key={gt.id}>
                  <TableCell>
                    <div className="flex items-center">
                      <Button
                        size="icon"
                        tooltip={t.moveUp}
                        variant="ghost"
                        onClick={() => handleMove(index, -1)}
                        disabled={isPending || index === 0}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        tooltip={t.moveDown}
                        variant="ghost"
                        onClick={() => handleMove(index, 1)}
                        disabled={isPending || index === goalTypes.length - 1}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{getLabel(gt.labelI18n)}</span>
                  </TableCell>
                  <TableCell>
                    <SvgPreview svg={gt.iconSvg} />
                  </TableCell>
                  {mandatesEnabled && <TableCell>{mandateModeLabel(gt.mandateMode)}</TableCell>}
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end">
                      <Button
                        size="icon"
                        tooltip={t.editGoalType}
                        variant="ghost"
                        onClick={() => openEdit(gt)}
                        disabled={isPending}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <TranslationButton
                        value={gt.labelI18n}
                        onSave={(labelI18n) => handleTranslationSave(gt, labelI18n)}
                        supportedLanguages={tenantSupportedLanguages}
                        disabled={isPending}
                      />
                      <Button
                        size="icon"
                        tooltip={t.delete}
                        variant="ghost"
                        onClick={() => {
                          setTypeToDelete(gt.id);
                          setConfirmDeleteOpen(true);
                        }}
                        disabled={isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingType
                ? editingTypeName
                  ? `${t.editGoalType} (${editingTypeName})`
                  : t.editGoalType
                : t.addGoalType}
            </DialogTitle>
            <DialogDescription>{t.createDialogDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="goal-type-label">{t.label}</Label>
              <Input
                id="goal-type-label"
                value={formLabel}
                onChange={(e) => setFormLabel(e.target.value)}
                placeholder={t.labelPlaceholder}
                disabled={isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="goal-type-icon">{t.iconLabel}</Label>
              <SvgIconField
                id="goal-type-icon"
                value={formIconSvg}
                onChange={setFormIconSvg}
                disabled={isPending}
              />
            </div>

            <div className="space-y-2">
              <Label>{t.allowedClientTypes}</Label>
              <div className="flex items-center gap-6">
                {ALL_CLIENT_TYPES.map((type) => {
                  const isChecked = formClientTypes.includes(type);
                  // Matches the supported-languages group in regional-settings:
                  // the last checked box is locked, and the label greys with it
                  // so the whole row reads as unavailable, not just the box.
                  const isLocked = formClientTypes.length === 1 && isChecked;
                  return (
                    <div key={type} className="flex items-center gap-2">
                      <Checkbox
                        id={`goalTypeClientType-${type}`}
                        checked={isChecked}
                        onCheckedChange={(checked) => {
                          setFormClientTypes((prev) =>
                            checked
                              ? ALL_CLIENT_TYPES.filter((ct) => prev.includes(ct) || ct === type)
                              : prev.filter((ct) => ct !== type),
                          );
                        }}
                        // Disabled rather than silently ignoring the click — a
                        // goal type available to nobody is not a state we allow,
                        // and a control that does nothing reads as broken.
                        // Checking another type re-enables this one.
                        disabled={isPending || isLocked}
                      />
                      <Label
                        htmlFor={`goalTypeClientType-${type}`}
                        className={`text-sm font-normal cursor-pointer${
                          isLocked ? " text-muted-foreground" : ""
                        }`}
                      >
                        {type === "Person"
                          ? t.clientTypePerson
                          : type === "Company"
                            ? t.clientTypeCompany
                            : t.clientTypeGroup}
                      </Label>
                    </div>
                  );
                })}
              </div>
              <p className="text-sm text-muted-foreground">{t.allowedClientTypesDescription}</p>
            </div>

            {mandatesEnabled && (
              <div className="space-y-2">
                <Label id="goal-type-mandate-mode-label">{t.mandate}</Label>
                {/* QL-760 — the mode has two values: a Disabled | Enabled segmented
                    control, Disabled ("hidden") first and the default. */}
                <ToggleGroup
                  type="single"
                  value={formMandateMode}
                  // Radix single mode emits "" when the active item is clicked again.
                  onValueChange={(next) => {
                    if (next) setFormMandateMode(next as MandateMode);
                  }}
                  disabled={isPending}
                  aria-labelledby="goal-type-mandate-mode-label"
                  className="w-fit"
                >
                  <ToggleGroupItem value="hidden">{t.mandateHidden}</ToggleGroupItem>
                  <ToggleGroupItem value="required">{t.mandateRequired}</ToggleGroupItem>
                </ToggleGroup>
                <p className="text-sm text-muted-foreground">{t.mandateDescription}</p>
              </div>
            )}

            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>

          <DialogFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button onClick={handleSubmit} loading={isPending}>
              {editingType ? t.save : t.addGoalType}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteGoalType}
        description={t.deleteDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
